import type { Language } from "../../contexts/LanguageContext";

export const settingsLabels: Record<Language, {
  pageHeader: {
    title: string;
    description: string;
  };
  resourcesSection: {
    header: string;
    description: string;
    manage: string;
  };
  language: {
    header: string;
    description: string;
    options: {
      label: string;
      pt: string;
      en: string;
      searchPlaceholder: string;
      emptyMessage: string;
    };
  };
  whatsappBanner: {
    header: string;
    description: string;
    configure: string;
  };
  companyInfo: {
    header: string;
    edit: string;
    loading: string;
    sections: {
      info: string;
      contact: string;
      address: string;
    };
    fields: {
      name: string;
      legalName: string;
      cpf: string;
      cnpj: string;
      phone: string;
      website: string;
      email: string;
      address: string;
      requireEvolution: string;
      yes: string;
      no: string;
    };
  };
  rooms: {
    header: string;
    new: string;
    loading: string;
    none: string;
    delete: string;
    capacitySuffix: string; // e.g. "• {n} simultaneous"
    newPage: {
      title: string;
      description: string;
      fields: {
        name: string;
        capacity: string;
      };
      back: string;
      cancel: string;
      save: string;
      saving: string;
      requiredError: string;
      requestError: string;
      success: string;
    };
  };
  exceptions: {
    header: string;
    sectionDescription: string;
    new: string;
    loading: string;
    none: string;
    delete: string;
    fields: {
      type: string;
      professional: string;
      allProfessionals: string;
      start: string;
      end: string;
      allDay: string;
      description: string;
    };
    toasts: {
      success: string;
      error: string;
      deleted: string;
      deleteError: string;
    };
  };
  specialties: {
    header: string;
    new: string;
    loading: string;
    none: string;
    delete: string;
    newPage: {
      title: string;
      description: string;
      fields: {
        name: string;
      };
      back: string;
      cancel: string;
      save: string;
      saving: string;
      requiredError: string;
      requestError: string;
      success: string;
    };
  };
  contract: {
    header: string;
    description: string;
    button: string;
    pageTitle: string;
    saveButton: string;
    saving: string;
    success: string;
    variablesTitle: string;
  };
  deleteAccount: {
    header: string;
    action: string;
    desc1: string;
    desc2: string;
    dialog: {
      title: string;
      description: string;
      cancel: string;
      confirm: string;
    };
    toast: string;
  };
  editCompanyPage: {
    title: string;
    back: string;
    loading: string;
    fields: {
      name: string;
      legalName: string;
      documentType: string;
      document: string;
      cpf: string;
      cnpj: string;
      phone: string;
      website: string;
      email: string;
      address: string;
      requireEvolution: string;
    };
    errors: { invalidCpf: string; invalidCnpj: string; emailRequired: string; invalidEmail: string };
    cancel: string;
    save: string;
    saving: string;
    comboboxSearch: string;
    comboboxEmpty: string;
  };
  whatsappConfig: {
    title: string;
    states: {
      connecting: string;
      connected: string;
      disconnected: string;
      error: string;
    };
    sendTypes: {
      daily: { title: string; desc: string };
      before: { title: string; desc: string };
      none: { title: string; desc: string };
    };
    fields: {
      time: string;
      hoursBefore: string;
      instanceId: string;
    };
    buttons: {
      connect: string;
      save: string;
      close: string;
    };
    toasts: {
      saved: string;
      saveError: string;
      qrGenerated: string;
      requestSent: string;
      connectError: string;
      noQr: string;
      details: string;
    };
qrDialog: {
      title: string;
      description: string;
      close: string;
      alt: string;
      noImg: string;
    };
  };
  insuranceProviders: {
    header: string;
    description: string;
    new: string;
    loading: string;
    none: string;
    default: string;
    delete: string;
    edit: string;
    manage: string;
    newPage: {
      title: string;
      description: string;
      fields: {
        name: string;
      };
      back: string;
      cancel: string;
      save: string;
      saving: string;
      requiredError: string;
      requestError: string;
      success: string;
    };
    toasts: {
      success: string;
      error: string;
      deleted: string;
      deleteError: string;
      updated: string;
      deleteDialog: {
        title: string;
        description: string;
        cancel: string;
        confirm: string;
      };
    };
  };
  resources: {
    title: string;
    description: string;
    loading: string;
    pricingSaved: string;
    savePricing: string;
    newSpecialtyPlaceholder: string;
    pricingTitle: string;
    perWeek: string;
    confirmDeleteRoom: string;
    confirmDeleteSpecialty: string;
    roomDeleted: string;
    specialtyDeleted: string;
    roomUpdated: string;
    updateRoomError: string;
    specialtyAdded: string;
    addSpecialtyError: string;
    suggestedPricing: string;
    registered: string;
    valueConfig: string;
  };
}> = {
  pt: {
    pageHeader: {
      title: "Configurações",
      description: "Gerencie as preferências da sua conta",
    },
    resourcesSection: {
      header: "Recursos da Empresa",
      description: "Gerencie suas salas e especialidades em um só lugar.",
      manage: "Gerenciar Recursos",
    },
    language: {
      header: "Idioma da plataforma",
      description: "Escolha como deseja utilizar a plataforma",
      options: {
        label: "Idioma",
        pt: "Português",
        en: "Inglês",
        searchPlaceholder: "Buscar idioma...",
        emptyMessage: "Nenhum idioma encontrado.",
      },
    },
    whatsappBanner: {
      header: "WhatsApp",
      description: "Conecte seu WhatsApp para avisar automaticamente seus clientes sobre as consultas.",
      configure: "Configurar",
    },
    companyInfo: {
      header: "Empresa",
      edit: "Editar",
      loading: "Carregando...",
      sections: {
        info: "Informações",
        contact: "Contato",
        address: "Endereço",
      },
      fields: {
        name: "Nome",
        legalName: "Razão social",
        cpf: "CPF",
        cnpj: "CNPJ",
        phone: "Telefone de contato",
        website: "Site",
        email: "E-mail da empresa",
        address: "Endereço",
        requireEvolution: "Obrigatório preencher evolução",
        yes: "Sim",
        no: "Não",
      },
    },
    rooms: {
      header: "Salas de atendimento",
      new: "Nova sala",
      loading: "Carregando...",
      none: "Nenhuma sala cadastrada",
      delete: "Excluir",
      capacitySuffix: "• {n} atendimentos simultâneos",
      newPage: {
        title: "Nova sala",
        description: "Cadastre uma nova sala para organizar seus atendimentos.",
        fields: {
          name: "Nome",
          capacity: "Capacidade (simultâneos)",
        },
        back: "Voltar",
        cancel: "Cancelar",
        save: "Salvar",
        saving: "Salvando...",
        requiredError: "Preencha nome e capacidade.",
        requestError: "Não foi possível criar a sala. Tente novamente.",
        success: "Sala criada com sucesso.",
      },
    },
    exceptions: {
      header: "Bloqueios e Fechamentos",
      sectionDescription: "Configure feriados, folgas e períodos em que a agenda deve ser bloqueada.",
      new: "Adicionar bloqueio",
      loading: "Carregando bloqueios...",
      none: "Nenhum bloqueio configurado",
      delete: "Excluir",
      fields: {
        type: "Tipo de bloqueio",
        professional: "Profissional",
        allProfessionals: "Toda a empresa (Fechado)",
        start: "Início",
        end: "Término",
        allDay: "Dia inteiro",
        description: "Motivo/Descrição",
      },
      toasts: {
        success: "Bloqueio adicionado com sucesso",
        error: "Erro ao adicionar bloqueio",
        deleted: "Bloqueio removido",
        deleteError: "Erro ao remover bloqueio",
      },
      tabs: {
        sessions: "Horários de Funcionamento",
        exceptions: "Bloqueios e Feriados",
      },
      sessions: {
        title: "Configuração de Horários",
        description: "Estes são os horários padrão de abertura da clínica.",
        open: "Aberto",
        closed: "Fechado",
        addPeriod: "Adicionar Período",
        noHours: "Nenhum horário configurado",
        aboutTitle: "Sobre os Horários Padrão",
        aboutDesc: "Estes são os horários em que sua clínica está aberta para novos agendamentos em dias normais. Bloqueios e Feriados na outra aba têm prioridade sobre estes horários.",
      }
    },
    specialties: {
      header: "Especialidades",
      new: "Nova especialidade",
      loading: "Carregando...",
      none: "Nenhuma especialidade cadastrada",
      delete: "Excluir",
      newPage: {
        title: "Nova especialidade",
        description: "Cadastre uma especialidade para associar aos atendimentos.",
        fields: {
          name: "Nome",
          room: "Sala (Opcional)",
          noneSelected: "Nenhuma sala vinculada",
        },
        back: "Voltar",
        cancel: "Cancelar",
        save: "Salvar",
        saving: "Salvando...",
        requiredError: "Informe o nome da especialidade.",
        requestError: "Não foi possível criar a especialidade. Tente novamente.",
        success: "Especialidade criada com sucesso.",
      },
    },
    resources: {
      title: "Recursos da Empresa",
      description: "Gerencie suas salas e especialidades em um só lugar.",
      tabs: {
        rooms: "Salas",
        specialties: "Especialidades",
      },
      loading: "Carregando recursos...",
      pricingSaved: "Configurações de valores salvas!",
      savePricing: "Salvar Valores",
      newSpecialtyPlaceholder: "Nova especialidade...",
      pricingTitle: "Preços Sugeridos",
      perWeek: "por semana",
      confirmDeleteRoom: "Tem certeza que deseja excluir esta sala?",
      confirmDeleteSpecialty: "Tem certeza que deseja excluir esta especialidade?",
      roomDeleted: "Sala excluída",
      specialtyDeleted: "Especialidade excluída",
      roomUpdated: "Sala atualizada!",
      updateRoomError: "Erro ao atualizar sala",
      specialtyAdded: "Especialidade adicionada",
      addSpecialtyError: "Erro ao adicionar especialidade",
      suggestedPricing: "Preços Sugeridos",
      registered: "Cadastradas",
      valueConfig: "Configuração de valores",
    },
    contract: {
      header: "Template de Contrato",
      description: "Configure o modelo de contrato padrão para seus clientes.",
      button: "Modelo de Contrato",
      pageTitle: "Editar Template de Contrato",
      saveButton: "Salvar Template",
      saving: "Salvando...",
      success: "Template salvo com sucesso!",
      variablesTitle: "Variáveis Disponíveis",
    },
    deleteAccount: {
      header: "Apagar conta definitivamente",
      action: "Apagar conta",
      desc1: "Apagar sua conta removerá permanentemente todos os seus dados (clientes, agendas, configurações e históricos).",
      desc2: "Esta ação é irreversível.",
      dialog: {
        title: "Apagar conta",
        description: "Tem certeza que deseja apagar sua conta? Tudo será apagado e não dá para recuperar.",
        cancel: "Cancelar",
        confirm: "Confirmar exclusão",
      },
      toast: "Sua conta foi marcada para exclusão. Em até 24h tudo será apagado.",
    },
    insuranceProviders: {
      header: "Convênios",
      description: "Gerencie os convênios aceitos pela sua empresa.",
      new: "Novo convênio",
      loading: "Carregando...",
      none: "Nenhum convênio cadastrado",
      default: "Padrão",
      delete: "Excluir",
      edit: "Editar",
      manage: "Gerenciar",
      newPage: {
        title: "Novo convênio",
        description: "Cadastre um novo convênio para associar aos atendimentos.",
        fields: {
          name: "Nome",
        },
        back: "Voltar",
        cancel: "Cancelar",
        save: "Salvar",
        saving: "Salvando...",
        requiredError: "Informe o nome do convênio.",
        requestError: "Não foi possível criar o convênio. Tente novamente.",
        success: "Convênio criado com sucesso.",
      },
      toasts: {
        success: "Convênio adicionado",
        error: "Erro ao adicionar convênio",
        deleted: "Convênio removido",
        deleteError: "Erro ao remover convênio",
        updated: "Convênio atualizado",
        deleteDialog: {
          title: "Excluir convênio",
          description: "Tem certeza que deseja excluir este convênio? Esta ação não pode ser desfeita.",
          cancel: "Cancelar",
          confirm: "Excluir",
        },
      },
    },
    editCompanyPage: {
      title: "Editar empresa",
      back: "Voltar",
      loading: "Carregando informações...",
      fields: {
        name: "Nome da empresa",
        legalName: "Razão social",
        documentType: "Tipo de documento",
        document: "Documento",
        cpf: "CPF",
        cnpj: "CNPJ",
        phone: "Telefone de contato",
        website: "Site",
        email: "E-mail da empresa",
        address: "Endereço",
        requireEvolution: "Obrigatório preencher evolução",
      },
      errors: {
        invalidCpf: "CPF inválido",
        invalidCnpj: "CNPJ inválido",
        emailRequired: "E-mail é obrigatório",
        invalidEmail: "E-mail inválido",
      },
      cancel: "Cancelar",
      save: "Salvar",
      saving: "Salvando...",
      comboboxSearch: "Buscar...",
      comboboxEmpty: "Nenhum item encontrado.",
    },
    whatsappConfig: {
      title: "Configurar WhatsApp",
      states: {
        connecting: "Conectando...",
        connected: "Conectado",
        disconnected: "Desconectado",
        error: "Erro",
      },
      sendTypes: {
        daily: { title: "Diário", desc: "Envia em um horário fixo todos os dias." },
        before: { title: "Antes de cada consulta", desc: "Envia X horas antes da consulta." },
        none: { title: "Não enviar", desc: "Desativa os envios automáticos." },
      },
      fields: {
        time: "Horário",
        hoursBefore: "Horas antes",
        instanceId: "ID da instância",
      },
      buttons: {
        connect: "Conectar WhatsApp",
        save: "Salvar",
        close: "Fechar",
      },
      toasts: {
        saved: "Configurações salvas",
        saveError: "Erro ao salvar",
        qrGenerated: "QR Code gerado. Escaneie no WhatsApp.",
        requestSent: "Solicitação enviada. Verifique seu dispositivo.",
        connectError: "Erro ao conectar WhatsApp",
        noQr: "Não foi possível carregar a imagem do QR Code.",
        details: "Detalhes técnicos",
      },
      qrDialog: {
        title: "Conectar WhatsApp",
        description: "Escaneie o QR Code abaixo com o WhatsApp do seu dispositivo.",
        close: "Fechar",
        alt: "QR Code do WhatsApp",
        noImg: "Não foi possível carregar a imagem do QR Code.",
      },
    },
  },
  en: {
    pageHeader: {
      title: "Settings",
      description: "Manage your account preferences",
    },
    resourcesSection: {
      header: "Company Resources",
      description: "Manage your rooms and specialties in one place.",
      manage: "Manage Resources",
    },
    language: {
      header: "Platform language",
      description: "Choose how you want to use the platform",
      options: {
        label: "Language",
        pt: "Portuguese",
        en: "English",
        searchPlaceholder: "Search language...",
        emptyMessage: "No languages found.",
      },
    },
    whatsappBanner: {
      header: "WhatsApp",
      description: "Connect your WhatsApp to automatically notify clients about appointments.",
      configure: "Configure",
    },
    companyInfo: {
      header: "Company",
      edit: "Edit",
      loading: "Loading...",
      sections: {
        info: "Information",
        contact: "Contact",
        address: "Address",
      },
      fields: {
        name: "Name",
        legalName: "Legal name",
        cpf: "CPF",
        cnpj: "CNPJ",
        phone: "Contact phone",
        website: "Website",
        email: "Company email",
        address: "Address",
        requireEvolution: "Require filling evolution",
        yes: "Yes",
        no: "No",
      },
    },
    rooms: {
      header: "Service rooms",
      new: "New room",
      loading: "Loading...",
      none: "No rooms registered",
      delete: "Delete",
      capacitySuffix: "• {n} simultaneous appointments",
      newPage: {
        title: "New room",
        description: "Register a new room to organize your appointments.",
        fields: {
          name: "Name",
          capacity: "Capacity (simultaneous)",
        },
        back: "Back",
        cancel: "Cancel",
        save: "Save",
        saving: "Saving...",
        requiredError: "Fill in name and capacity.",
        requestError: "Could not create the room. Try again.",
        success: "Room created successfully.",
      },
    },
    exceptions: {
      header: "Blocks and Closures",
      sectionDescription: "Configure holidays, time off and periods when the schedule should be blocked.",
      new: "Add block",
      loading: "Loading blocks...",
      none: "No blocks configured",
      delete: "Delete",
      fields: {
        type: "Block type",
        professional: "Professional",
        allProfessionals: "Whole company (Closed)",
        start: "Start",
        end: "End",
        allDay: "All day",
        description: "Reason/Description",
      },
      toasts: {
        success: "Block added successfully",
        error: "Error adding block",
        deleted: "Block removed",
        deleteError: "Error removing block",
      },
      tabs: {
        sessions: "Business Hours",
        exceptions: "Blocks and Holidays",
      },
      sessions: {
        title: "Hours Configuration",
        description: "These are the standard opening hours of the clinic.",
        open: "Open",
        closed: "Closed",
        addPeriod: "Add Period",
        noHours: "No hours configured",
        aboutTitle: "About Standard Hours",
        aboutDesc: "These are the hours your clinic is open for new appointments on normal days. Blocks and Holidays in the other tab take precedence over these hours.",
      }
    },
    specialties: {
      header: "Specialties",
      new: "New specialty",
      loading: "Loading...",
      none: "No specialties registered",
      delete: "Delete",
      newPage: {
        title: "New specialty",
        description: "Register a specialty to link to your appointments.",
        fields: {
          name: "Name",
          room: "Room (Optional)",
          noneSelected: "No room linked",
        },
        back: "Back",
        cancel: "Cancel",
        save: "Save",
        saving: "Saving...",
        requiredError: "Enter the specialty name.",
        requestError: "Could not create the specialty. Try again.",
        success: "Specialty created successfully.",
      },
    },
    resources: {
      title: "Company Resources",
      description: "Manage your rooms and specialties in one place.",
      tabs: {
        rooms: "Rooms",
        specialties: "Specialties",
      },
      loading: "Loading resources...",
      pricingSaved: "Pricing configurations saved!",
      savePricing: "Save Pricing",
      newSpecialtyPlaceholder: "New specialty...",
      pricingTitle: "Suggested Pricing",
      perWeek: "per week",
      confirmDeleteRoom: "Are you sure you want to delete this room?",
      confirmDeleteSpecialty: "Are you sure you want to delete this specialty?",
      roomDeleted: "Room deleted",
      specialtyDeleted: "Specialty deleted",
      roomUpdated: "Room updated!",
      updateRoomError: "Error updating room",
      specialtyAdded: "Specialty added",
      addSpecialtyError: "Error adding specialty",
      suggestedPricing: "Suggested Pricing",
      registered: "Registered",
      valueConfig: "Value configuration",
    },
    contract: {
      header: "Contract Template",
      description: "Configure the default contract model for your clients.",
      button: "Contract Template",
      pageTitle: "Edit Contract Template",
      saveButton: "Save Template",
      saving: "Saving...",
      success: "Template saved successfully!",
      variablesTitle: "Available Variables",
    },
    deleteAccount: {
      header: "Permanently delete account",
      action: "Delete account",
      desc1: "Deleting your account will permanently remove all your data (clients, schedules, settings, and history).",
      desc2: "This action is irreversible.",
      dialog: {
        title: "Delete account",
        description: "Are you sure you want to delete your account? Everything will be erased and cannot be recovered.",
        cancel: "Cancel",
        confirm: "Confirm deletion",
      },
      toast: "Your account was marked for deletion. Within 24h everything will be erased.",
    },
    insuranceProviders: {
      header: "Insurance Providers",
      description: "Manage the insurance providers accepted by your company.",
      new: "New provider",
      loading: "Loading...",
      none: "No insurance providers registered",
      default: "Default",
      delete: "Delete",
      edit: "Edit",
      manage: "Manage",
      newPage: {
        title: "New Insurance Provider",
        description: "Register a new insurance provider for appointments.",
        fields: {
          name: "Name",
        },
        back: "Back",
        cancel: "Cancel",
        save: "Save",
        saving: "Saving...",
        requiredError: "Enter the insurance provider name.",
        requestError: "Could not create the provider. Try again.",
        success: "Provider created successfully.",
      },
      toasts: {
        success: "Provider added",
        error: "Error adding provider",
        deleted: "Provider removed",
        deleteError: "Error removing provider",
        updated: "Provider updated",
        deleteDialog: {
          title: "Delete provider",
          description: "Are you sure you want to delete this provider? This cannot be undone.",
          cancel: "Cancel",
          confirm: "Delete",
        },
      },
    },
    editCompanyPage: {
      title: "Edit company",
      back: "Go back",
      loading: "Loading company information...",
      fields: {
        name: "Company name",
        legalName: "Legal name",
        documentType: "Document type",
        document: "Document",
        cpf: "CPF",
        cnpj: "CNPJ",
        phone: "Contact phone",
        website: "Website",
        email: "Company email",
        address: "Address",
        requireEvolution: "Require filling evolution",
      },
      errors: {
        invalidCpf: "Invalid CPF",
        invalidCnpj: "Invalid CNPJ",
        emailRequired: "Email is required",
        invalidEmail: "Invalid email",
      },
      cancel: "Cancel",
      save: "Save",
      saving: "Saving...",
      comboboxSearch: "Search...",
      comboboxEmpty: "No items found.",
    },
    whatsappConfig: {
      title: "Configure WhatsApp",
      states: {
        connecting: "Connecting...",
        connected: "Connected",
        disconnected: "Disconnected",
        error: "Error",
      },
      sendTypes: {
        daily: { title: "Daily", desc: "Sends at a fixed time every day." },
        before: { title: "Before each appointment", desc: "Sends X hours before the appointment." },
        none: { title: "Do not send", desc: "Disables automatic sending." },
      },
      fields: {
        time: "Time",
        hoursBefore: "Hours before",
        instanceId: "Instance ID",
      },
      buttons: {
        connect: "Connect WhatsApp",
        save: "Save",
        close: "Close",
      },
      toasts: {
        saved: "Settings saved",
        saveError: "Error while saving",
        qrGenerated: "QR Code generated. Scan it in WhatsApp.",
        requestSent: "Request sent. Check your device.",
        connectError: "Error connecting WhatsApp",
        noQr: "Could not load the QR Code image.",
        details: "Technical details",
      },
      qrDialog: {
        title: "Connect WhatsApp",
        description: "Scan the QR Code below using your device's WhatsApp.",
        close: "Close",
        alt: "WhatsApp QR Code",
        noImg: "Could not load the QR Code image.",
      },
    },
  },
};
